// src/components/HeroSection.jsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Image } from "lucide-react";
import { motion } from "framer-motion";

import heroVideo from "../assets/hero.mp4";

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground h-[calc(100dvh-64px)] md:h-[calc(100dvh-80px)]">
      {/* Pattern overlay (lighter) */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='0.25'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-10 h-full flex flex-col md:flex-row items-center justify-center">
        {/* ================= DESKTOP TEXT ================= */}
        <div
          className="hidden md:block w-full md:max-w-[440px] lg:max-w-[520px]
          px-6 sm:px-8 md:px-12 lg:px-20
          pt-10 md:pt-0 pb-4 md:pb-0
          z-20 text-center md:text-left order-1"
        >
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-white"
          >
            <span className="bg-gradient-to-r from-white via-slate-300 to-slate-500 bg-clip-text text-transparent">
              Precision Engineering.
            </span>
            <br />
            <span className="bg-gradient-to-r from-white via-slate-300 to-slate-500 bg-clip-text text-transparent">
              Stainless Excellence.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3 }}
            className="mt-6 text-lg md:text-xl text-white/80 max-w-md"
          >
            Redefining industrial fabrication with{" "}
            <span className="font-semibold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              architectural precision
            </span>{" "}
            and{" "}
            <span className="font-semibold bg-gradient-to-r from-gray-200 to-gray-400 bg-clip-text text-transparent">
              high-performance standards
            </span>
            .
          </motion.p>

          {/* DESKTOP BUTTONS */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="hidden md:flex mt-10 items-center gap-4 flex-nowrap"
          >
            <Link
              to="/products"
              className="inline-flex items-center gap-2 whitespace-nowrap shrink-0 bg-white text-primary font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition"
            >
              <Package size={18} />
              View Products
              <ArrowRight size={16} />
            </Link>

            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 whitespace-nowrap shrink-0 border border-white/30 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition"
            >
              <Image size={18} />
              View Gallery
            </Link>
          </motion.div>
        </div>

        {/* ================= MOBILE HERO ================= */}
        <div className="md:hidden absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>

          {/* MOBILE TEXT */}
          <div className="absolute inset-0 flex flex-col justify-end px-6 pb-10">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="font-display text-4xl font-bold leading-tight text-white"
              style={{ textShadow: "0 4px 20px rgba(0,0,0,1)" }}
            >
              Precision Engineering.
              <br />
              <span className="text-white">
                Stainless Excellence.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="mt-4 text-gray-100 text-sm leading-relaxed max-w-sm"
              style={{ textShadow: "0 4px 20px rgba(0,0,0,1)" }}
            >
              Redefining industrial fabrication with architectural precision and
              high-performance standards.
            </motion.p>
          </div>
        </div>

        {/* ================= DESKTOP VIDEO ================= */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="hidden md:block relative flex-[3.5] w-full md:w-auto h-full order-2"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover clip-droop"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>

          {/* subtle glow only (no shade) */}
          <div className="absolute -z-10 w-[120%] h-[120%] bg-green-primary/20 blur-3xl rounded-full"></div>
        </motion.div>
      </div>

      {/* Clip path */}
      <style>{`
        @media (min-width: 768px) {
          .clip-droop {
            clip-path: polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
